/**
 * WebSocket client for the simulation server.
 *
 * Connects to `/ws` on the page's own host (vite proxies it to :3020 in dev),
 * parses `ServerMessage`s and re-emits them. Reconnects with capped exponential
 * backoff and reports its status so the UI can show a live / reconnecting dot.
 */
import type { HelloMessage, ServerMessage, TickMessage } from '@village/shared';

export type ConnStatus = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'mock';

export interface NetEvents {
  hello: HelloMessage;
  tick: TickMessage;
  status: ConnStatus;
}

type Handler<K extends keyof NetEvents> = (payload: NetEvents[K]) => void;

/** Tiny typed event emitter — all the pub/sub this app needs. */
export class Emitter {
  private handlers: { [K in keyof NetEvents]?: Set<Handler<K>> } = {};

  on<K extends keyof NetEvents>(type: K, fn: Handler<K>): () => void {
    let set = this.handlers[type] as Set<Handler<K>> | undefined;
    if (!set) {
      set = new Set();
      (this.handlers[type] as Set<Handler<K>>) = set;
    }
    set.add(fn);
    return () => set!.delete(fn);
  }

  emit<K extends keyof NetEvents>(type: K, payload: NetEvents[K]): void {
    const set = this.handlers[type] as Set<Handler<K>> | undefined;
    if (!set) return;
    for (const fn of [...set]) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[net] handler for "${type}" threw`, err);
      }
    }
  }
}

export interface NetOptions {
  /** called after this many consecutive failed connections (used to fall back to the mock in dev) */
  onGiveUp?: (attempts: number) => void;
  giveUpAfter?: number;
}

const BASE_DELAY = 600;
const MAX_DELAY = 15_000;

export class Net extends Emitter {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private timer: number | null = null;
  private stopped = false;
  private gaveUp = false;
  status: ConnStatus = 'connecting';

  constructor(private opts: NetOptions = {}) {
    super();
  }

  static url(): string {
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${scheme}://${location.host}/ws`;
  }

  connect(): void {
    if (this.stopped) return;
    this.clearTimer();
    this.setStatus(this.attempts === 0 ? 'connecting' : 'reconnecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(Net.url());
    } catch (err) {
      console.warn('[net] could not open socket', err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.attempts = 0;
      this.setStatus('live');
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data) as ServerMessage;
      } catch {
        console.warn('[net] dropped unparseable frame');
        return;
      }
      if (msg && msg.type === 'hello') this.emit('hello', msg);
      else if (msg && msg.type === 'tick') this.emit('tick', msg);
    };

    ws.onerror = () => {
      /* onclose always follows; handled there */
    };

    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      if (this.stopped) return;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.attempts++;
    const limit = this.opts.giveUpAfter ?? 0;
    if (limit && this.attempts >= limit && !this.gaveUp) {
      this.gaveUp = true;
      this.opts.onGiveUp?.(this.attempts);
    }
    if (this.stopped) return;
    this.setStatus('reconnecting');
    // capped exponential backoff with a little jitter so reloads don't sync up
    const delay = Math.min(MAX_DELAY, BASE_DELAY * 2 ** Math.min(this.attempts - 1, 6));
    const jittered = delay * (0.8 + Math.random() * 0.4);
    this.timer = window.setTimeout(() => this.connect(), jittered);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  setStatus(status: ConnStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit('status', status);
  }

  stop(): void {
    this.stopped = true;
    this.clearTimer();
    this.ws?.close();
    this.ws = null;
  }
}
